import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ShieldCheck } from 'lucide-react'
import Navbar from '@/components/navbar/Navbar'
import OutcomeTiles from '../components/landing-page/OutcomeTiles'
import FeatureTiles from '../components/landing-page/FeatureTiles'
import HeroPanel from '../components/landing-page/HeroPanel'
import { useAuth } from '../hooks/authentication/useAuth'

export default function LandingPage() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { isAuthenticated, isLoading, login, error: authError, clearError, msalReady, retryInitialization } = useAuth()
    const [loginError, setLoginError] = useState(null)

    useEffect(() => {
        const error = searchParams.get('error')
        const message = searchParams.get('message')

        if (error) {
            setLoginError(message || 'Authentication failed. Please try again.')
            window.history.replaceState({}, document.title, '/')
        }
    }, [searchParams])

    useEffect(() => {
        if (authError) {
            setLoginError(authError)
        }
    }, [authError])

    const handleGetStarted = async () => {
        if (isAuthenticated) {
            navigate('/flows/root')
        } else if (authError && !msalReady) {
            try {
                setLoginError(null)
                await retryInitialization()
            } catch (err) {
                console.error('Retry error:', err)
                setLoginError(err.message || 'Failed to retry. Please refresh the page.')
            }
        } else {
            try {
                setLoginError(null)
                if (clearError) clearError()
                await login()
            } catch (err) {
                console.error('Login error:', err)
                setLoginError(err.message || 'Failed to login. Please try again.')
            }
        }
    }

    const section1Ref = useRef(null)
    const section2Ref = useRef(null)

    const scrollToSection = (ref) => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const ctaLabel = isLoading && !authError
        ? 'Loading...'
        : authError && !msalReady
            ? 'Retry Login'
            : isAuthenticated
                ? 'Get Started'
                : 'Login to Get Started'

    return (
        <div className="landing-black-bg">
            <div className="landing-page-bg">
                <Navbar
                    scrollToSection={scrollToSection}
                    sections={{ section1Ref, section2Ref }}
                    mainPage={1}
                />

                <section className="landing-hero">
                    <div className="landing-hero-layout">
                        <div className="landing-hero-copy">
                            <h1 className="landing-hero-title">
                                <span className="landing-hero-title-amplify" aria-label="Amplify">
                                    <span aria-hidden="true">Ampl</span>
                                    <span className="amp-i" aria-hidden="true">
                                        &#x131;
                                        <img src="/amplify.png" alt="" className="amp-i-dot" />
                                    </span>
                                    <span aria-hidden="true">fy</span>
                                </span>
                                <span className="landing-hero-title-accent text-heading-accent">Digital Ads</span>
                            </h1>
                            <p className="landing-hero-subtitle">
                                Our comprehensive digital ad solution, driving local traffic and boosting
                                your online presence.
                            </p>

                            {(loginError || authError) && (
                                <div className="landing-hero-error bg-red-50 border-l-4 border-red-500 rounded p-3 shadow-sm">
                                    <p className="text-red-700 text-sm font-medium">{loginError || authError}</p>
                                </div>
                            )}

                            <button
                                className="land-cta-1 landing-hero-cta"
                                data-testid="hero-cta"
                                onClick={handleGetStarted}
                                disabled={isLoading && !authError}
                            >
                                {isLoading && !authError ? (
                                    <span className="flex items-center gap-2 justify-center">
                                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                                        Loading...
                                    </span>
                                ) : (
                                    ctaLabel
                                )}
                            </button>

                            <p className="landing-hero-trust">
                                <ShieldCheck size={15} strokeWidth={2} />
                                Secure single sign-on · No setup required
                            </p>
                        </div>

                        <HeroPanel />
                    </div>
                </section>
            </div>

            <div className="second-body">
                <div ref={section1Ref}>
                    <OutcomeTiles />
                </div>
                <div ref={section2Ref}>
                    <FeatureTiles />
                </div>

                <section className="landing-cta-band">
                    <h2 className="landing-cta-band-title">Ready to amplify your digital ads?</h2>
                    <p className="landing-cta-band-desc">
                        Plan smarter campaigns, optimize spend, and measure what matters.
                    </p>
                    <button
                        className="land-cta-1 landing-cta-band-btn"
                        onClick={handleGetStarted}
                        disabled={isLoading || !msalReady}
                    >
                        {isLoading || !msalReady ? 'Loading...' : isAuthenticated ? 'Get Started' : 'Login to Get Started'}
                    </button>
                </section>
            </div>
        </div>
    )
}
